import { defer } from 'react-router-dom'

import { useGlobalDataStore } from '@stores/globalData/globalData.store'
import Session from '@utils/Session'
import ApiRoutes from '@utils/api-routes'
import http from '@utils/http'

import type Collection from '@models/Collection'
import type { ErrorResponse } from '@utils/http'
import type { LoaderFunction, ShouldRevalidateFunction } from 'react-router-dom'

export type PrivateStatisticsResponse = {
  activity_stream_last_viewed_at: number
  archived_scores_count: number
  favorites_count: number
  marketplace_scores_count: number
  messages_not_read: number
  most_recent_activity_event_id: string
  my_books_count: number
  private_scores_count: number
  public_scores_count: number
  purchased_scores_count: number
  shared_scores_count: number
  trash_count: number
}

export type PrivateCollectionsResponse = {
  collections: Collection[]
}

export type Chapter = {
  title: string
  sections: { type: string; html?: string; externalId?: string; title?: string }[]
}

export type RedeemedContent = {
  id: number
  type: string
  title: string
  bookBeginHTML: string
  bookEndHTML: string
  chapters: Chapter[]
}

export type RedeemedContentResponse = {
  redemptions: RedeemedContent[]
}

export type HomeLoaderResponse = {
  statistics: Promise<PrivateStatisticsResponse>
  collections: Promise<PrivateCollectionsResponse>
  redeemedContent: Promise<RedeemedContentResponse> | null
}

const homeLoader: LoaderFunction = async () => {
  const { serverData, globalData } = useGlobalDataStore.getState()
  if (!globalData.session.isLoggedIn()) {
    // TODO: When sign in is in new frontend, change this to:
    // return redirect('/users/sign_in')
    return new Promise(() => {
      window.location.replace('/users/sign_in')
    })
  }

  let redeemedContent: Promise<RedeemedContentResponse | ErrorResponse> | null = null

  if (serverData) {
    const session = new Session(serverData.login, serverData.client_settings)
    if (session.gatedFeatures.showGatedFeature('bookDemoUsers')) {
      redeemedContent = http.loader.get<RedeemedContentResponse>(
        ApiRoutes.contentItemPath('book_demo')
      )
    }
  }

  const statistics = http.loader.post<PrivateStatisticsResponse>(
    ApiRoutes.members.privateStatisticsPath()
  )
  const collections = http.loader.post<PrivateCollectionsResponse>(
    ApiRoutes.members.privateCollectionsPath()
  )
  return defer({ statistics, collections, redeemedContent })
}

export default homeLoader

export const homeRevalidator: ShouldRevalidateFunction = ({
  currentUrl,
  formMethod,
  formAction,
}) => {
  if (formAction === '/home/updateCollectionOrder') return false
  // Revalidate if sending POST requests via actions
  if (formMethod === 'post') {
    return true
  }

  // Do not run loader when navigating within children routes
  return !currentUrl.pathname.startsWith('/home')
}
